/* eslint-disable react/jsx-no-duplicate-props */
import { Box, Typography, styled } from "@mui/material";
import PLImage from "components/patternLib/PLImage";
import PLModal from "components/patternLib/PLModal";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { formatDateTime } from "translations/formatter";
import { AssetImageCarousel } from "..";
import ImageGalleryCarousel from "./ImageGalleryCarousel";
import useHandleImageZoom from "./useHandleImageZoom";
import { useRecoilState } from "recoil";
import { imageGalleryCursorAtom, imageGalleryHasZoomAtom, imageGalleryImageZoomTransformAtom } from "./imageZoomState";
import translations from "translations";
import { Maybe } from "graphqlBase/types";

const StyledDataTileImageGalleryWrapper = styled(Box)({
  display: "flex",
  columnGap: "12px",
  rowGap: "12px",
  marginTop: "30px",
  flexWrap: "wrap",
});

const StyledImageGalleryImageWrapper = styled("div")(({ hasZoom, cursor }: { cursor: string; hasZoom: boolean }) => ({
  display: "flex",
  zIndex: hasZoom ? 1000 : "auto",
  position: "relative",
  overflow: "hidden",
  width: "fit-content",
  padding: hasZoom ? "2%" : 0,
  cursor,
  justifyContent: "center",
  alignItems: "center",
}));

const commonStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

const ImageGallery: React.FC<{ comment?: Maybe<string> | undefined; images: AssetImageCarousel[] }> = ({
  images,
  comment,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string>("");
  const [cursor, setCursor] = useRecoilState(imageGalleryCursorAtom);
  const [imageTransform, setImageTransform] = useRecoilState(imageGalleryImageZoomTransformAtom);
  const [hasZoom, setHasZoom] = useRecoilState(imageGalleryHasZoomAtom);

  const imageContainerRef = useRef<HTMLImageElement>(null);

  const mapImages: { [key: string]: AssetImageCarousel } = useMemo(() => {
    return images.reduce((obj, item) => ({ ...obj, [item.id]: item }), {});
  }, [images]);

  const { handleZoom, handleNavigateZoomedImage, handleMouseEnterImage } = useHandleImageZoom({
    imageContainerRef,
    openModal,
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Remove mouse down logic since we don't need dragging anymore
      if (hasZoom) {
        e.preventDefault();
      }
    },
    [hasZoom]
  );

  const handleSelectImage = (imageId: string) => {
    setSelectedImageId(imageId);
    setCursor("default");
    setImageTransform({ x: 0, y: 0, scale: 1 });
    setHasZoom(false);
  };

  return (
    <>
      <StyledDataTileImageGalleryWrapper>
        {images.map((image) => (
          <Box key={image.id}>
            <PLImage
              objectFit="contain"
              alt={image.alt}
              src={image.src}
              height="47px"
              width="64px"
              onClick={() => {
                setSelectedImageId(image.id);
                setOpenModal(true);
              }}
            />
          </Box>
        ))}
      </StyledDataTileImageGalleryWrapper>
      <PLModal
        allowCloseOnBackdrop
        modalTitle="Image Gallery"
        lhClose={() => setOpenModal(false)}
        show={openModal}
        width="60%"
        height="fit-content"
        hideCancel
        hideSubmit
        onMouseMove={handleNavigateZoomedImage}
      >
        <>
          <Box slot="modal-content">
            {selectedImageId && (
              <Box
                sx={{
                  ...commonStyles,
                  rowGap: "2rem",
                  height: "30rem",
                  width: "100%",
                }}
              >
                <StyledImageGalleryImageWrapper
                  cursor={cursor}
                  hasZoom={hasZoom}
                  onMouseDown={handleMouseDown}
                  onClick={handleZoom}
                  onMouseEnter={handleMouseEnterImage}
                  ref={imageContainerRef}
                >
                  <img
                    alt={mapImages[selectedImageId].alt}
                    src={mapImages[selectedImageId].src}
                    style={{
                      objectFit: "contain",
                      overflow: "hidden",
                      transform: `scale(${imageTransform.scale}) translate(${
                        imageTransform.x / imageTransform.scale
                      }px, ${imageTransform.y / imageTransform.scale}px)`,
                      transition: "transform 0.1s ease-out",
                      pointerEvents: "none",
                      transformOrigin: "center center",
                      height: "100%",
                      width: "100%",
                    }}
                  />
                </StyledImageGalleryImageWrapper>
                <Box sx={{ ...commonStyles, marginBottom: "2rem" }}>
                  {comment ? <Typography variant="copyBold">{comment}</Typography> : null}
                  <Typography variant="copy">
                    {formatDateTime(new Date(mapImages[selectedImageId].createdAt))}
                  </Typography>
                </Box>
              </Box>
            )}
            <ImageGalleryCarousel
              images={images}
              selectedImageId={selectedImageId}
              handleSelectImage={handleSelectImage}
            />
          </Box>
        </>
      </PLModal>
    </>
  );
};

export default ImageGallery;
